import {
  SVButton,
  SVPasswordInput,
  SVTextInput,
  SVForm,
  SVLink,
} from '@/components/core';
import { ROUTES } from '@/constants/routes';
import { useInitiateAuth } from '@/hooks/requests/useAuth';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import showToast from '@/utils/toast';
import { Card, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { MESSAGES } from '@/constants/messages';
import { useQueryClient } from '@tanstack/react-query';
import { updateLastLoginDate } from '@/services/user';
import { logAnalyticsEvent } from '@/utils/analytics';
import { toast } from 'react-toastify';
import LogoImage from '@/components/LogoImage';
import { FORM_FIELDS } from '@/constants/formFields';
import { HealthMetrics } from '@/utils/sentry/healthMetrics';

type LoginForm = {
  username: string;
  password: string;
};

export default function Login() {
  const queryClient = useQueryClient();
  const formMethods = useForm<LoginForm>({
    defaultValues: {
      username: '',
      password: '',
    },
  });
  const { isStaff, setAuthUser, setPostLoginScreen } = useAuthUser();
  const { initateAuth, isLoadingInitateAuth } = useInitiateAuth();
  const navigate = useNavigate();

  const handleError = (isServerError?: boolean) => {
    showToast({
      type: 'error',
      data: {
        content: isServerError
          ? MESSAGES.genericError
          : "We can't find that username and password",
      },
    });
  };

  async function handleSubmit(data: LoginForm) {
    try {
      const response = await initateAuth(
        {
          ...data,
          username: data.username.toLowerCase(),
          userGroup: isStaff ? 'staff' : 'teammate',
        },
        {
          onError: () => handleError(true),
        },
      );

      if (!response.success) {
        HealthMetrics.trackLogin(false, 'email');
        logAnalyticsEvent({
          category: 'error',
          action: 'loginError',
        });
        handleError();
        return;
      }

      setAuthUser(response);
      setPostLoginScreen(window.location.search);

      if (response.success && response.idToken) {
        HealthMetrics.trackLogin(true, 'email');
        HealthMetrics.setUserContext(data.username, data.username);
        logAnalyticsEvent({
          category: 'success',
          action: 'loginSuccess',
        });
        updateLastLoginDate();
        queryClient.invalidateQueries();
        toast.dismiss();
        navigate(ROUTES.HOMEPAGE);
      } else {
        navigate(ROUTES.OTP, { state: data });
      }
    } catch (error) {
      HealthMetrics.trackLogin(false, 'email');
      if (error instanceof Error) {
        HealthMetrics.trackError(error, {
          tags: {
            flow_type: 'authentication',
            action: 'login',
          },
        });
      }
      logAnalyticsEvent({
        category: 'error',
        action: 'loginError',
      });
      handleError(true);
    }
  }

  return (
    <>
      <LogoImage />
      <Card>
        <Stack spacing={2}>
          <Typography variant="h5">
            {isStaff ? 'Staff Login' : 'Login'}
          </Typography>
          <SVForm<LoginForm> methods={formMethods} onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <SVTextInput
                name="username"
                label="Email"
                rules={{ required: true }}
              />
              <SVPasswordInput
                name="password"
                bypassValidation
                {...FORM_FIELDS.PASSWORD}
              />
              <Stack direction="row" justifyContent="space-between">
                <SVLink
                  style={{ marginBottom: '10px' }}
                  to={ROUTES.FORGOT_PASSWORD}>
                  Forgot Password?
                </SVLink>
              </Stack>
              <SVButton isLoading={isLoadingInitateAuth} type="submit">
                Log In
              </SVButton>
            </Stack>
          </SVForm>
        </Stack>
      </Card>
    </>
  );
}
